import { Component, OnInit, Inject, computed } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ApiService } from '@app/core/services/api/api.service';
import { OrderBatchService } from '@app/services/order-batch/order-batch.service';
import { ShipmentService } from '@app/services/shipment/shipment.service';
import { OrderBatch } from '@app/services/order-batch/order-batch.model';
import { SaveAsBatchNewComponent } from '../save-as-batch-new/save-as-batch-new.component';
import { CarrierTypeNames } from '@pdqdev/pdq-shared/dist/CarrierData';
import { Carrier, CarrierTypes } from '@app/services/carriers/carrier.model';
import { ShopService } from '@app/services/shops/shop.service';
import { map, takeUntil, tap } from 'rxjs/operators';
import { CarrierService } from '@app/services/carriers/carrier.service';
import { CarriersService } from '@app/pages/delivery-service/services';
import { TextDialogComponent } from '@app/sharedModule/components/text-dialog/text-dialog.component';
import { Subject } from 'rxjs';
import { openAbroadAssignBatchWindow } from 'src/utils';
import { SaveasbatchComponent } from '@pages/workspace/assign/components/saveasbatch/saveasbatch.component';
import { ComponentType } from '@angular/cdk/portal';
import { Shop } from '@pdqdev/pdq-shared/dist/entities/Shop';
import { filterUsableCarriers } from '@utils/carrier-utils';
import { enableBestMatchByShop } from '@app/pages/checkout-app/utils';
import moment from 'moment';
import { OrderBatchDTO } from '@pdqdev/pdq-shared/dist/entities';
import { ActivatedRoute } from '@angular/router';

interface ICarrierRowData {
  id: number;
  type: CarrierTypes;
  name: string;
  isTestMode?: boolean;
  averageDeliveryTimeInMsec: number | null;
  disabled?: boolean;
}

@Component({
  selector: 'assign-to-carrier',
  templateUrl: './assign-to-carrier.html',
  styleUrls: ['./assign-to-carrier.scss'],
  standalone: false,
})
export class AssignToCarrier implements OnInit {
  private destroy$ = new Subject();
  private shopMetadata;
  private orderIds: string[] = [];
  rows: ICarrierRowData[] = [];
  carriers: Carrier[] = [];
  saveAsBatchConfig: {
    component: ComponentType<SaveasbatchComponent | SaveAsBatchNewComponent>;
    dialogSettings: {}; // dynamic mat-dialog settings
  };
  selectedCarrierId: number;
  loadingSmartBatch: boolean = false;
  loadingBestBatch: boolean = false;
  loadingCarriers: boolean = true;
  dummyLoadingTexts = ['Working our magic', 'Getting the best rates', 'Quickly cross-checking'];
  dummyLoadingTextSecondsCounter = 0;
  secondsDurationForDummyLoadingText = 5;
  breathingDotsIndex = 0;
  breathingDotsMaxLength = 3;
  isClearRatesCalculationInterval = false;
  ratesCalculationInfoText = '';
  bestMatchCalculationText = 'Collecting data...';
  enableBestMatch = true;
  sortIcon: 'default' | 'goes_down' | 'goes_up' = 'default';
  shop: Shop;
  activeTab;
  enableAddToBatch;
  batches: (OrderBatch & { mismatchError: string })[];
  filteredBatches: OrderBatch[];
  selectedBatchId: number;
  loadingBatches: boolean;
  columns = ['', 'Batch details', 'Pickup', 'Fulfillment status', 'Owner', 'Label status', ''];

  // Feature flag for paginated add to batch
  usePaginatedAddToBatch = computed(() => this.initUsePaginatedFulfillment());

  constructor(
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public apiService: ApiService,
    private orderBatchService: OrderBatchService,
    private shipmentService: ShipmentService,
    private shopService: ShopService,
    public dialogRef: MatDialogRef<any>,
    public errorDialogRef: MatDialogRef<TextDialogComponent>,
    private carrierService: CarrierService,
    private carriersService: CarriersService,
    private activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.loadingCarriers = true;
    this.initShopService();
    this.initCarriersList();
  }

  initCarriersList() {
    this.carriersService.loadCarriersByShop().subscribe((carriers) => {
      this.carriers = carriers;
      this.rows = this.initiateCarriersData(carriers);

      this.loadingCarriers = false;
    });
  }

  private initUsePaginatedFulfillment() {
    if (
      this.shopService.shopSignal()?.metadata?.usePaginatedFulfillmentV2 ||
      this.activatedRoute.snapshot.queryParams['usePaginatedFulfillmentV2'] === 'true'
    ) {
      return true;
    }
    return !this.shopService.shopSignal()?.metadata?.disablePaginatedFulfillmentV2;
  }

  private initShopService() {
    this.shopService.shop$.pipe(takeUntil(this.destroy$)).subscribe((shop) => {
      this.shopMetadata = shop?.metadata;
      this.shop = shop;
      const supportNewAssignBatchWindow =
        openAbroadAssignBatchWindow(shop?.address?.country ?? shop?.address?.countryCode) ||
        this.shopMetadata?.supportNewAssignBatchWindow;
      this.initSaveAsBatchConfig(supportNewAssignBatchWindow);
      this.enableBestMatch =
        supportNewAssignBatchWindow && !this.checkIfOnlyPickupOrders() && enableBestMatchByShop(shop);
      this.enableAddToBatch = this.shopMetadata?.addOrdersToExistingBatches;
    });
  }

  initBatches() {
    this.loadingBatches = true;
    this.orderBatchService
      .fetchPrintingBatches({ fetchShippingAddress: true })
      .pipe(
        takeUntil(this.destroy$),
        map((batches) => {
          return this.orderBatchService.getBatchesByState(batches, this.shopMetadata);
        }),
      )
      .subscribe((batches) => {
        this.batches = batches?.fulfillmentReady
          .filter((batch) => batch.orders?.length)
          .map((batch) => ({
            ...batch,
            mismatchError: [
              this.checkDifferentShipFromLocations(batch),
              this.checkMixPickupVsShipping(batch),
              this.checkInternationalVsDomestic(batch),
              this.checkPassedPickupDate(batch),
            ]
              .filter(Boolean)
              .join('\n'),
          }));
        this.loadingBatches = false;
      });
  }

  checkDifferentShipFromLocations(batch) {
    const shipFromId = batch.shipFromAddress?.id;
    return this.data.selected.some((order) => order.shipFromAddress?.id != shipFromId)
      ? '*Orders from different inventories can’t be batched'
      : '';
  }

  checkMixPickupVsShipping(batch) {
    const isPickupBatch = !!(batch.orders[0].pickUpLocationId && !batch.orders[0].shippingAddress);

    return [...this.data.selected, ...batch.orders].some(
      (order) => !!(order.pickUpLocationId && !order.shippingAddress) !== isPickupBatch,
    )
      ? '*Pickup & Carrier orders cant be batch together'
      : '';
  }

  checkInternationalVsDomestic(batch) {
    const shopCountry = this.shop?.address?.country ?? this.shop?.address?.countryCode;
    const batchShipFromCountry = batch.shipFromAddress?.countryCode || batch.shipFromAddress?.country || shopCountry;
    const batchOrder = batch.orders[0];
    const isInternationalBatch =
      !!batchOrder.shippingAddress &&
      batchOrder.shippingAddress.countryCode !== batchShipFromCountry &&
      batchOrder.shippingAddress.country !== batchShipFromCountry;
    return this.data.selected.some((o) => {
      const shipFromCountry = o.shipFromAddress?.countryCode || o.shipFromAddress?.country || shopCountry;
      const isInternational =
        !!o.shippingAddress &&
        o.shippingAddress.countryCode !== shipFromCountry &&
        o.shippingAddress.country !== shipFromCountry;
      return isInternational !== isInternationalBatch;
    })
      ? '*International & Domestic orders cant be batch together'
      : '';
  }

  checkPassedPickupDate(batch: OrderBatch) {
    const shopTimezone = this.shop.metadata?.ianaTimeZone ?? 'UTC';
    const todayInShopTimezone = moment.tz(new Date(), shopTimezone).startOf('day');
    const pickupDateInShopTimezone = moment.tz(batch.pickupDate, shopTimezone).startOf('day');
    return pickupDateInShopTimezone.isBefore(todayInShopTimezone)
      ? '*Cant add orders to batch with passed pickup date'
      : '';
  }

  onFilteredBatchesChanged(filteredBatches) {
    this.filteredBatches = filteredBatches;
  }

  initSaveAsBatchConfig(supportNewWindow: boolean) {
    if (supportNewWindow) {
      this.saveAsBatchConfig = {
        component: SaveAsBatchNewComponent,
        dialogSettings: {
          disableClose: true,
          maxWidth: '100vw',
          width: '100vw',
          maxHeight: '88vh',
          height: '88vh',
          backdropClass: '',
          panelClass: 'save-as-batch-dialog',
          position: { bottom: '0%' },
        },
      };
    } else {
      this.saveAsBatchConfig = {
        component: SaveasbatchComponent,
        dialogSettings: {
          disableClose: true,
          backdropClass: 'global_modal_overlay',
        },
      };
    }
  }

  private initiateCarriersData(receivedCarriers: Carrier[]): ICarrierRowData[] {
    let tempCarriers: ICarrierRowData[] = [];
    const rows: ICarrierRowData[] = [];
    receivedCarriers = receivedCarriers?.filter((receivedCarrier) => receivedCarrier?.isActive) ?? [];
    receivedCarriers =
      receivedCarriers?.filter((receivedCarrier) => receivedCarrier?.type?.custom_fields?.shippable !== false) ?? [];
    receivedCarriers = filterUsableCarriers(receivedCarriers, this.shop, this.shopMetadata);
    const subTenantIdToIsExists = this.createSubTenantsMap();
    receivedCarriers.forEach((carrier) => {
      const tempCarrier: ICarrierRowData = {
        id: carrier.id,
        type: carrier.type,
        name: carrier.name,
        isTestMode: carrier.isTestMode,
        averageDeliveryTimeInMsec: null,
      };
      if (this.shopService.isMultiMerchant) {
        tempCarrier.disabled = carrier?.eCommerceSubTenants?.every((subTenant) => !subTenantIdToIsExists[subTenant.id]);
      }
      tempCarriers.push(tempCarrier);
    });

    if (this.shopMetadata?.isMultiParcelSupport) {
      tempCarriers = tempCarriers.filter((el: any) => el.type.name !== CarrierTypeNames.SHIPPO_MA_DOMESTIC);
    }
    tempCarriers.sort((a, b) => (a.disabled ? 1 : -1));
    rows.push(...this.filterPickupOrders(tempCarriers));
    return rows;
  }

  onSelectedTabChanged(event) {
    this.activeTab = event;
    // Only load batches via old method when feature flag is off
    if (this.activeTab === 'batches' && !this.batches && !this.usePaginatedAddToBatch()) {
      this.initBatches();
    }
  }

  private createSubTenantsMap(): { [key: string]: boolean } {
    const subTenantIdToIsExists: { [key: string]: boolean } = {};
    if (!this.shopService.isMultiMerchant) {
      return subTenantIdToIsExists;
    }

    this.data.selected
      .filter((order) => order.eCommerceSubTenant)
      .forEach((order) => {
        subTenantIdToIsExists[order.eCommerceSubTenant.id] = true;
      });

    return subTenantIdToIsExists;
  }
  private filterPickupOrders(rows: ICarrierRowData[]) {
    if (this.checkIfOnlyPickupOrders()) {
      return rows.filter((carrierData) => {
        return carrierData.type?.name?.toLowerCase?.()?.indexOf?.('self') !== -1;
      });
    } else {
      return rows;
    }
  }

  private checkIfOnlyPickupOrders(): boolean {
    return this.data.selected.every((order) => order.pickUpLocationId && !order.shippingAddress);
  }

  addToBatch(selectedBatch: OrderBatch | OrderBatchDTO): void {
    this.selectedBatchId = selectedBatch.id;
    this.data.additionalName = null;
    this.openDialog();
  }

  assignToCarrier(selectedCarrier: ICarrierRowData): void {
    this.selectedCarrierId = selectedCarrier.id;
    if (!selectedCarrier.type?.name?.includes(CarrierTypeNames.SELF)) {
      this.data.selected = this.data.selected.filter(
        (order) => order.pickUpLocationId == null || order.shippingAddress,
      );
    }
    this.openDialog();
  }

  openDialog(): void {
    const noRefreshOrdersBatch: boolean = true;
    this.orderIds = this.data.selected.map((order) => order.id);

    this.startModifyingRatesCalculationText();

    this.loadingSmartBatch = true;
    this.orderBatchService
      .printOrdersBatch(
        this.selectedCarrierId,
        this.orderIds,
        { disableCountersEmit: noRefreshOrdersBatch, addToBatchId: this.selectedBatchId },
        // Here we intentially do not send service level id to backend
        // Important - if you change here the payload, modify the error section as well
      )
      .pipe(
        tap((result: OrderBatch) => {
          const totalRatesData = result?.rateInfo?.totalServiceLevelsRatesData;
          const isCarrierGeneratesRates = result?.rateInfo?.generatedRatesData;
          if (
            !result ||
            (isCarrierGeneratesRates && (!totalRatesData || !totalRatesData.selectedServiceLevelsRates?.length))
          ) {
            throw new Error(`no valid rates, error`);
          }
        }),
      )
      .subscribe(
        (result: OrderBatch) => {
          if (this.data.additionalName) {
            const name = result.name.split(',');
            result.name = `${name[0]} - ${this.data.additionalName},${name[1]}`;
            result['additionalName'] = this.data.additionalName;
          }

          this.saveAsBatchConfig.dialogSettings['data'] = {
            ids: this.orderIds,
            batch: result,
            addToBatchId: this.selectedBatchId,
          };

          const dialogRef = this.dialog.open(this.saveAsBatchConfig.component, {
            ...this.saveAsBatchConfig.dialogSettings,
          });
          this.loadingSmartBatch = false;
          this.selectedCarrierId = null;
          this.selectedBatchId = null;

          dialogRef.afterClosed().subscribe((result) => {
            if (result?.closeDialog) {
              this.dialogRef.close();
            }
          });
        },
        (error) => {
          console.log('error occured');
          console.log(error);
          this.orderBatchService
            .deleteUnConfirmedBatchByTimeout(this.selectedCarrierId, this.orderIds, {
              disableCountersEmit: noRefreshOrdersBatch,
            })
            .subscribe(
              (res) => {},
              (error) => {
                console.error(error);
              },
            );
          console.log('opening error dialog1');
          this.loadingSmartBatch = false;
          this.selectedCarrierId = null;
          this.selectedBatchId = null;
          this.terminateRatesCalculationTextRunning();
          console.log('opening error dialog2');
          return this.openErrorDialog();
        },
      );
  }

  openBestMatch(): void {
    const noRefreshOrdersBatch: boolean = true;
    this.orderIds = this.data.selected.map((order) => order.id);

    // this.startModifyingRatesCalculationText();

    this.loadingBestBatch = true;
    this.orderBatchService
      .printOrdersBatch(
        null,
        this.orderIds,
        { disableCountersEmit: noRefreshOrdersBatch, bestMatchInit: true },
        // Here we intentially do not send service level id to backend
        // Important - if you change here the payload, modify the error section as well
      )
      .pipe(
        tap((result: OrderBatch) => {
          const totalRatesData = result?.rateInfoList?.map((rateInfo) => rateInfo.totalServiceLevelsRatesData);
          const isCarrierGeneratesRates = result?.rateInfoList?.some((rateInfo) => rateInfo.generatedRatesData);
          if (
            !result ||
            (isCarrierGeneratesRates && !totalRatesData) /*|| !totalRatesData.selectedServiceLevelsRates?.length*/
          ) {
            throw new Error(`no valid rates, error`);
          }
        }),
      )
      .subscribe(
        (result: OrderBatch) => {
          if (this.data.additionalName) {
            const name = result.name.split(',');
            result.name = `${name[0]} - ${this.data.additionalName},${name[1]}`;
            result['additionalName'] = this.data.additionalName;
          }

          this.saveAsBatchConfig.dialogSettings['data'] = {
            ids: this.orderIds,
            batch: result,
          };

          const dialogRef = this.dialog.open(this.saveAsBatchConfig.component, {
            ...this.saveAsBatchConfig.dialogSettings,
          });
          this.loadingBestBatch = false;
          this.selectedCarrierId = null;

          dialogRef.afterClosed().subscribe((result) => {
            if (result?.closeDialog) {
              this.dialogRef.close();
            }
          });
        },
        (error) => {
          console.log(error);
          this.orderBatchService
            .deleteUnConfirmedBatchByTimeout(null, this.orderIds, {
              disableCountersEmit: noRefreshOrdersBatch,
              bestMatchInit: true,
            })
            .subscribe(
              (res) => {},
              (error) => {
                console.error(error);
              },
            );
          this.loadingBestBatch = false;
          this.selectedCarrierId = null;
          this.terminateRatesCalculationTextRunning();
          return this.openErrorDialog();
        },
      );
  }

  changeDeliveryServicesSort(): void {
    switch (this.sortIcon) {
      case 'default':
      case 'goes_up':
        this.sortIcon = 'goes_down';
        this.rows = this.rows?.sort((a, b) => {
          if (a?.name.toLowerCase() < b?.name.toLowerCase()) {
            return -1;
          } else if (a?.name.toLowerCase() > b?.name.toLowerCase()) {
            return 1;
          } else return 0;
        });
        break;
      case 'goes_down':
        this.sortIcon = 'goes_up';
        this.rows = this.rows.sort((a, b) => {
          if (a?.name?.toLowerCase() > b?.name?.toLowerCase()) {
            return -1;
          } else if (a?.name?.toLowerCase() < b?.name.toLowerCase()) {
            return 1;
          } else return 0;
        });
        break;
    }
  }

  private openErrorDialog() {
    this.errorDialogRef = this.dialog.open(TextDialogComponent, {
      width: '37vw',
      maxWidth: '80vw',
      backdropClass: 'global_modal_overlay_subdued',
      panelClass: 'overflow-visible-class',
      data: {
        bodyTexts: ['Sorry for the inconvenience. Please try again.'],
        headerText: 'Something went wrong',
        confirmButtonText: 'Continue',
      },
    });
    this.errorDialogRef.afterClosed().subscribe(() => {
      this.dialog.closeAll();
    });
  }

  private startModifyingRatesCalculationText() {
    this.isClearRatesCalculationInterval = false;
    this.dummyLoadingTextSecondsCounter = 0;
    this.breathingDotsIndex = 0;

    this.modifyRatesCalculationText();
  }

  private modifyRatesCalculationText() {
    if (this.isClearRatesCalculationInterval) {
      return;
    }

    const currentDummyLoadingTextIndex = Math.floor(
      this.dummyLoadingTextSecondsCounter / this.secondsDurationForDummyLoadingText,
    );
    if (currentDummyLoadingTextIndex < this.dummyLoadingTexts.length) {
      this.ratesCalculationInfoText = this.dummyLoadingTexts[currentDummyLoadingTextIndex];
      this.dummyLoadingTextSecondsCounter++;
    }

    this.ratesCalculationInfoText =
      this.ratesCalculationInfoText.split('.')[0] + '.'.repeat(this.breathingDotsIndex + 1);
    this.breathingDotsIndex = (this.breathingDotsIndex + 1) % this.breathingDotsMaxLength;

    setTimeout(() => this.modifyRatesCalculationText(), 1000);
  }

  private terminateRatesCalculationTextRunning() {
    this.isClearRatesCalculationInterval = true;
    this.ratesCalculationInfoText = '';
  }
}
